import React from "react";
import { Image, Linking, StyleSheet, View } from "react-native";
import { Text } from "native-base";
import FastImage from "@d11/react-native-fast-image";

import { webFont } from "../../../../../Universal/API/API";
import PressableWrapp from "../../../../../Universal/Buttons/PressableReact/PressableWrapp";

interface CatalogPdfCardProps {
  title: string;
  pdfUrl: string;
  coverImage?: string | null;
  chips?: string[];
  scaleAll: number;
  width: string;
}

const PDF_THUMBNAIL_CACHE = new Map<string, string | null>();
const PDF_WORKER_SRC = "https://unpkg.com/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs";
const PDF_THUMBNAIL_ERROR_LOGGED = new Set<string>();

const canRenderPdfThumbnailInCurrentOrigin = (pdfUrl: string): boolean => {
  if (typeof window === "undefined") return false;

  try {
    const parsed = new URL(pdfUrl, window.location.href);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
};

const usePdfThumbnail = (pdfUrl: string | undefined, fallbackImage?: string | null) => {
  const [thumbnailUrl, setThumbnailUrl] = React.useState<string | null>(() => {
    if (!pdfUrl) return fallbackImage ?? null;
    if (PDF_THUMBNAIL_CACHE.has(pdfUrl)) {
      return PDF_THUMBNAIL_CACHE.get(pdfUrl) ?? fallbackImage ?? null;
    }
    return fallbackImage ?? null;
  });

  React.useEffect(() => {
    let cancelled = false;

    if (!pdfUrl || typeof window === "undefined" || typeof document === "undefined") {
      setThumbnailUrl(fallbackImage ?? null);
      return;
    }

    if (!canRenderPdfThumbnailInCurrentOrigin(pdfUrl)) {
      PDF_THUMBNAIL_CACHE.set(pdfUrl, fallbackImage ?? null);
      setThumbnailUrl(fallbackImage ?? null);
      return;
    }

    if (PDF_THUMBNAIL_CACHE.has(pdfUrl)) {
      setThumbnailUrl(PDF_THUMBNAIL_CACHE.get(pdfUrl) ?? fallbackImage ?? null);
      return;
    }

    const renderThumbnail = async () => {
      try {
        const pdfjsLib: any = await import("pdfjs-dist/build/pdf.mjs");

        if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
          pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
        }

        const loadingTask = pdfjsLib.getDocument({
          url: pdfUrl,
          withCredentials: false,
          useWorkerFetch: true,
        });

        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = Math.min(1.4, 520 / Math.max(baseViewport.width, 1));
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d", { alpha: false });

        if (!context) {
          throw new Error("Canvas context unavailable");
        }

        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);

        const renderTask = page.render({
          canvasContext: context,
          viewport,
        });

        await renderTask.promise;

        const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
        PDF_THUMBNAIL_CACHE.set(pdfUrl, dataUrl);

        if (!cancelled) {
          setThumbnailUrl(dataUrl);
        }
      } catch (error) {
        if (!PDF_THUMBNAIL_ERROR_LOGGED.has(pdfUrl)) {
          PDF_THUMBNAIL_ERROR_LOGGED.add(pdfUrl);
          console.log("pdf thumbnail error:", error);
        }
        PDF_THUMBNAIL_CACHE.set(pdfUrl, fallbackImage ?? null);
        if (!cancelled) {
          setThumbnailUrl(fallbackImage ?? null);
        }
      }
    };

    void renderThumbnail();

    return () => {
      cancelled = true;
    };
  }, [fallbackImage, pdfUrl]);

  return thumbnailUrl;
};

const createStyles = (scaleAll: number) =>
  StyleSheet.create({
    card: {
      borderRadius: 18 * scaleAll,
      overflow: "hidden",
      backgroundColor: "#FFFFFF",
      borderWidth: 1,
      borderColor: "#ECE8E1",
    },
    cardHovered: {
      shadowColor: "#000000",
      shadowOpacity: 0.12,
      shadowRadius: 24,
      shadowOffset: { width: 0, height: 12 },
      elevation: 8,
    },
    mediaWrap: {
      position: "relative",
      width: "100%",
      aspectRatio: 1.08,
      overflow: "hidden",
      backgroundColor: "#F6F3EE",
      borderBottomWidth: 1,
      borderBottomColor: "#ECE8E1",
    },
    image: {
      width: "100%",
      height: "100%",
    },
    placeholder: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#F4F1EB",
    },
    placeholderText: {
      fontFamily: webFont("Inter"),
      fontSize: 15 * scaleAll,
      color: "#7A746C",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.5,
    },
    badgeRow: {
      position: "absolute",
      left: 10 * scaleAll,
      right: 10 * scaleAll,
      bottom: 10 * scaleAll,
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 8 * scaleAll,
    },
    metaChip: {
      minHeight: 24 * scaleAll,
      borderRadius: 999,
      paddingHorizontal: 8 * scaleAll,
      paddingVertical: 4 * scaleAll,
      backgroundColor: "#F7F5F1",
      borderWidth: 1,
      borderColor: "#ECE8E1",
      alignItems: "center",
      justifyContent: "center",
    },
    metaChipDark: {
      backgroundColor: "#111111",
      borderColor: "#111111",
    },
    metaChipWhite: {
      backgroundColor: "rgba(255,255,255,0.94)",
      borderColor: "rgba(236,232,225,0.96)",
    },
    metaChipText: {
      fontFamily: webFont("Inter"),
      fontSize: 9 * scaleAll,
      color: "#3C3732",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.45,
    },
    metaChipTextDark: {
      color: "#FFFFFF",
    },
    body: {
      paddingHorizontal: 14 * scaleAll,
      paddingTop: 12 * scaleAll,
      paddingBottom: 14 * scaleAll,
      backgroundColor: "#FFFFFF",
    },
    eyebrow: {
      fontFamily: webFont("Inter"),
      fontSize: 9.5 * scaleAll,
      lineHeight: 12 * scaleAll,
      color: "#7C756C",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.5,
      marginBottom: 6 * scaleAll,
    },
    title: {
      fontFamily: webFont("Inter"),
      fontSize: 15 * scaleAll,
      lineHeight: 19 * scaleAll,
      color: "#111111",
      fontWeight: "700",
      marginBottom: 8 * scaleAll,
    },
    metaRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 8 * scaleAll,
      marginBottom: 12 * scaleAll,
    },
    openButton: {
      minHeight: 38 * scaleAll,
      borderRadius: 999,
      backgroundColor: "#FFFFFF",
      borderWidth: 1,
      borderColor: "#111111",
      paddingHorizontal: 14 * scaleAll,
      paddingVertical: 8 * scaleAll,
      alignItems: "center",
      justifyContent: "center",
    },
    openButtonText: {
      fontFamily: webFont("Inter"),
      fontSize: 11.5 * scaleAll,
      lineHeight: 14 * scaleAll,
      color: "#111111",
      fontWeight: "700",
      letterSpacing: 0.35,
      textTransform: "uppercase",
    },
  });

const CatalogPdfCard: React.FC<CatalogPdfCardProps> = ({
  title,
  pdfUrl,
  coverImage,
  chips = [],
  scaleAll,
  width,
}) => {
  const styles = React.useMemo(() => createStyles(scaleAll), [scaleAll]);
  const [hovered, setHovered] = React.useState(false);
  const thumbnailUrl = usePdfThumbnail(pdfUrl, coverImage ?? null);
  const resolvedImage = thumbnailUrl ?? coverImage ?? null;
  const isInlinePreview = !!resolvedImage && resolvedImage.startsWith("data:");

  const handleOpenPdf = React.useCallback(async () => {
    if (!pdfUrl) return;

    try {
      const canOpen = await Linking.canOpenURL(pdfUrl);
      if (!canOpen) return;
      await Linking.openURL(pdfUrl);
    } catch (error) {
      console.log("openPdf error:", error);
    }
  }, [pdfUrl]);

  return (
    <PressableWrapp
      onPress={handleOpenPdf}
      activeOpacity={0.86}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={{ width }}
    >
      <View style={[styles.card, hovered ? styles.cardHovered : null]}>
        <View style={styles.mediaWrap}>
          {resolvedImage ? (
            isInlinePreview ? (
              <Image source={{ uri: resolvedImage }} style={styles.image} resizeMode="cover" />
            ) : (
              <FastImage
                source={{ uri: resolvedImage, cache: "immutable" as any }}
                fallback={true}
                style={styles.image}
                resizeMode="cover"
              />
            )
          ) : (
            <View style={styles.placeholder}>
              <Text selectable={false} style={styles.placeholderText}>
                PDF
              </Text>
            </View>
          )}

          {chips.length > 0 ? (
            <View style={styles.badgeRow}>
              {chips.slice(0, 2).map((chip) => (
                <View key={chip} style={[styles.metaChip, styles.metaChipWhite]}>
                  <Text selectable={false} style={styles.metaChipText}>
                    {chip}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}
        </View>

        <View style={styles.body}>
          <Text selectable={false} style={styles.eyebrow}>
            PDF-каталог
          </Text>

          <Text selectable={false} style={styles.title} numberOfLines={2}>
            {title}
          </Text>

          <View style={styles.openButton}>
            <Text selectable={false} style={styles.openButtonText}>
              Открыть PDF
            </Text>
          </View>
        </View>
      </View>
    </PressableWrapp>
  );
};

export default React.memo(CatalogPdfCard);
